import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import socket from 'websockets/socket';
import moment from 'moment-timezone';
import { v4 as uuidV4 } from 'uuid';
import { uiActions } from 'careand-redux/slices/ui/ui';
import type { ChatSavedPromptRequest, PromptRequest, AssistantMessage } from '../generated/api';
import {
  type ExtraArgs,
  type PromptOutput,
  type PromptInput,
  type PromptName,
  runSavedPromptTyped as runSavedPromptTypedThunk,
} from '../thunks/runSavedPromptTyped';
import { type CareandPrompts } from '../generated/prompts';

function getStreamingKey(id?: string) {
  return `assistant-${id}`;
}

type StreamingData = {
  index: number;
  text: string;
  streamingKey?: string;
};

type Streams = Record<string, StreamingData[]>;

const EMPTY_STREAM: StreamingData[] = [];
const DEFAULT_ARRAY: string[] = [];

function useAssistant(id?: string, manageReduxState?: boolean, suggestReplies?: boolean) {
  const dispatch = useAppDispatch();
  const socketConnection = useAppSelector((state) => state.resource.socketConnection);

  const reduxAssistant = useAppSelector((state) => state.ui.assistants.find((it) => it.id === id));

  const initialMessages = useMemo(() => {
    return reduxAssistant?.messages ?? [];
  }, [reduxAssistant]);

  const defaultStreamingKey = useMemo(() => getStreamingKey(id), [id]);

  // Per-call streaming buffers, keyed by streamingKey. Concurrent calls each
  // accumulate into their own slot so they can't trample each other.
  const [streams, setStreams] = useState<Streams>(() => ({ [defaultStreamingKey]: [] }));
  // Keys whose API call is still in flight.
  const [loadingSet, setLoadingSet] = useState<Set<string>>(() => new Set());
  // Most recent call without a custom streamingKey. streamingText/loadingReply
  // expose this slot — callers that pass their own key run in isolation.
  const [primaryKey, setPrimaryKey] = useState<string>(defaultStreamingKey);

  const [localMessages, setLocalMessages] = useState<AssistantMessage[]>(initialMessages);
  const expectedRequest = useRef<string | undefined>();
  const [replySuggestions, setSuggestions] = useState<string[]>([]);
  const suggestionKey = useRef<string | undefined>();
  const [loadingReplySuggestions, setLoadingReplySuggestions] = useState(false);

  const subject = reduxAssistant?.subject;

  useEffect(() => {
    setPrimaryKey(defaultStreamingKey);
    setStreams((prev) =>
      defaultStreamingKey in prev ? prev : { ...prev, [defaultStreamingKey]: [] },
    );
  }, [defaultStreamingKey]);

  const loadingReply = loadingSet.has(primaryKey);

  useEffect(() => {
    if (manageReduxState) {
      dispatch(uiActions.updateAssistant({ id, messages: localMessages }));
    }
  }, [dispatch, id, manageReduxState, localMessages]);

  useEffect(() => {
    if (manageReduxState) {
      dispatch(uiActions.updateAssistant({ id, loading: loadingReply }));
    }
  }, [dispatch, id, manageReduxState, loadingReply]);

  useEffect(() => {
    if (manageReduxState && !subject && localMessages.length === 1) {
      generateChatSubject(localMessages[0]!.content).then((subject) => {
        if (subject) {
          dispatch(uiActions.updateAssistant({ id, subject }));
        }
      });
    }
  }, [dispatch, id, manageReduxState, localMessages, subject]);

  const messages = useMemo(() => {
    return manageReduxState ? reduxAssistant?.messages ?? [] : localMessages;
  }, [manageReduxState, reduxAssistant, localMessages]);

  useEffect(() => {
    const eventName = 'ai-streaming';
    let instance = socket.getSocket();

    const listener = (data: StreamingData) => {
      const streamKey = data.streamingKey;
      if (!streamKey) return;
      setStreams((prev) => {
        if (!(streamKey in prev)) return prev;
        const existing = prev[streamKey] ?? EMPTY_STREAM;
        const base = data.index === 0 ? EMPTY_STREAM : existing;
        return { ...prev, [streamKey]: [...base, data] };
      });
    };

    if (socketConnection === 'connected' && instance) {
      instance.on(eventName, listener);
    }
    return () => {
      if (instance) instance.removeListener(eventName, listener);
    };
  }, [socketConnection]);

  const beginStream = useCallback((key: string, makePrimary: boolean) => {
    setStreams((prev) => ({ ...prev, [key]: [] }));
    setLoadingSet((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    if (makePrimary) setPrimaryKey(key);
  }, []);

  const endStream = useCallback((key: string) => {
    setLoadingSet((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setStreams((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const regenerateSuggestions = useCallback(() => {
    const key = uuidV4();
    suggestionKey.current = key;
    const chatStr = messages.map((it) => `${it.role}: ${it.content}`).join('\n\n');

    setLoadingReplySuggestions(true);
    runSavedPromptTypedThunk('Chat Reply Suggestions', { chat: chatStr }, {})
      .then((result) => {
        if (result) setSuggestions(result);
      })
      .catch((err) => {
        /*ignore*/
      })
      .finally(() => setLoadingReplySuggestions(false));
  }, [messages]);

  useEffect(() => {
    const message = messages[messages.length - 1];
    if (!message || !suggestReplies || replySuggestions?.length > 0) {
      return;
    }

    if (message.role === 'assistant') {
      regenerateSuggestions();
    } else {
      setSuggestions(DEFAULT_ARRAY);
    }
  }, [messages, suggestReplies, regenerateSuggestions, replySuggestions]);

  const clearMessages = useCallback(() => {
    setLocalMessages([]);
  }, []);

  const stopRequest = useCallback(() => {
    expectedRequest.current = undefined;
    setLoadingSet(new Set());
    setStreams({ [defaultStreamingKey]: [] });
    setPrimaryKey(defaultStreamingKey);
  }, [defaultStreamingKey]);

  const prompt2 = useCallback(
    async (
      aiPrompt: PromptRequest,
      patient_id?: string,
      fields?: PromptRequest['fields'],
      document_id?: string,
      budget_tokens?: number,
    ) => {
      beginStream(defaultStreamingKey, true);
      let response = await dispatch(
        api.AI.prompt2({
          promptRequest: {
            name: aiPrompt.name,
            prompt: aiPrompt.prompt,
            context: aiPrompt.context,
            provider: aiPrompt.provider,
            model: aiPrompt.model,
            include_main_context: aiPrompt.include_main_context,
            fields,
            patient_id,
            document_id,
            budget_tokens,
            allowed_tools: aiPrompt.allowed_tools,
          },
          streamingKey: defaultStreamingKey,
        }),
        { errorTitle: 'AI Request' },
      );
      endStream(defaultStreamingKey);
      return response?.value;
    },
    [dispatch, defaultStreamingKey, beginStream, endStream],
  );

  const chatSavedPrompt = useCallback(
    async (data: ChatSavedPromptRequest) => {
      let initialDate = moment().unix();
      setLocalMessages([]);
      beginStream(defaultStreamingKey, true);
      const requestId = uuidV4();
      expectedRequest.current = requestId;
      let result = await dispatch(
        api.AI.chatSavedPrompt({ ...data, streamingKey: defaultStreamingKey }),
        { errorTitle: 'AI Chat' },
      );
      if (result && expectedRequest.current === requestId) {
        const parsed = result.map((message, index) => {
          message.date = index === result.length - 1 ? moment().unix() : initialDate;
          return message;
        });
        setLocalMessages(parsed);
      }
      if (expectedRequest.current === requestId) {
        expectedRequest.current = undefined;
      }
      endStream(defaultStreamingKey);
      return result;
    },
    [dispatch, defaultStreamingKey, beginStream, endStream],
  );

  const runSavedPromptTyped = useCallback(
    async <T extends PromptName>(
      name: T,
      input: PromptInput<CareandPrompts[T]>,
      extra: ExtraArgs<CareandPrompts[T]> & {
        streamingKey?: string;
        budgetTokens?: number;
      },
    ): Promise<PromptOutput<CareandPrompts[T]> | undefined> => {
      // Auto-generate a key when none is provided so concurrent calls don't
      // share a buffer. Calls with their own key stay out of the primary slot.
      const callKey = extra.streamingKey ?? uuidV4();
      const makePrimary = !extra.streamingKey;

      beginStream(callKey, makePrimary);
      const result = await runSavedPromptTypedThunk(name, input, {
        ...extra,
        streamingKey: callKey,
      });
      endStream(callKey);

      return result;
    },
    [beginStream, endStream],
  );

  const chatAssistant = useCallback(
    async (text: string, clear = false) => {
      const message: AssistantMessage = {
        content: text,
        role: 'user',
        date: moment().unix(),
      };

      // Update messages first
      setLocalMessages((prevMessages) => (clear ? [message] : [...prevMessages, message]));

      // Get the messages we need for the API call
      const newMessages = clear ? [message] : [...localMessages, message]; // Or use a ref to track current messages

      beginStream(defaultStreamingKey, true);
      const requestId = uuidV4();
      expectedRequest.current = requestId;

      const result = await dispatch(
        api.AI.chatAssistant({
          streamingKey: defaultStreamingKey,
          assistantConversation: { messages: newMessages },
        }),
        { errorTitle: 'AI Chat' },
      );

      // Check if this request is still the expected one
      if (result && expectedRequest.current === requestId) {
        result.date = moment().unix();
        setLocalMessages((prevMsgs) => [...prevMsgs, result]);
      }

      if (expectedRequest.current === requestId) {
        expectedRequest.current = undefined;
      }
      endStream(defaultStreamingKey);
    },
    [dispatch, defaultStreamingKey, localMessages, beginStream, endStream],
  );

  const lastReply = useMemo(() => {
    let assistantMessages = messages.filter((message) => message.role === 'assistant');
    return assistantMessages[assistantMessages.length - 1];
  }, [messages]);

  const streamingText = useMemo(() => {
    const data = streams[primaryKey] ?? EMPTY_STREAM;
    return data.map(({ text }) => text).join('');
  }, [streams, primaryKey]);

  const generateChatSubject = useCallback(async (firstMessage: string) => {
    return await runSavedPromptTypedThunk('Chat Subject Generation', { firstMessage }, {});
  }, []);

  return {
    prompt2,
    chatSavedPrompt,
    chatAssistant,
    streamingText,
    lastReply,
    messages,
    loadingReply,
    clearMessages,
    stopRequest,
    runSavedPromptTyped,
    replySuggestions,
    regenerateSuggestions,
    loadingReplySuggestions,
  };
}

export default useAssistant;
